// ============================================
// Form Persistence Context
// Preserves form state across navigations AND full page refreshes.
// - In-memory Map for the hot path (no localStorage writes per keystroke-
//   level state change; just the ref read).
// - localStorage mirror so drafts survive: hard refresh, PWA eviction,
//   iOS Safari tab recycling, Capacitor WebView memory purges.
// - Cleared only by explicit clear() (on wizard X / successful save).
// ============================================

import React, { createContext, useContext, useRef, useCallback } from 'react';

type FormState = Record<string, unknown>;

interface FormPersistContextValue {
  /** Get saved state for a form. Returns undefined if none. */
  get: (formId: string) => FormState | undefined;
  /** Save state for a form. Call this on every state change. */
  set: (formId: string, state: FormState) => void;
  /** Clear saved state for a form. Call after successful submission. */
  clear: (formId: string) => void;
}

const FormPersistContext = createContext<FormPersistContextValue>({
  get: () => undefined,
  set: () => {},
  clear: () => {},
});

export const useFormPersist = () => useContext(FormPersistContext);

const LS_PREFIX = 'prtcl:form:';

function lsKey(formId: string) {
  return `${LS_PREFIX}${formId}`;
}

function lsRead(formId: string): FormState | undefined {
  try {
    const raw = localStorage.getItem(lsKey(formId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as FormState) : undefined;
  } catch {
    return undefined;
  }
}

function lsWrite(formId: string, state: FormState) {
  try {
    localStorage.setItem(lsKey(formId), JSON.stringify(state));
  } catch {
    // Quota exceeded or private browsing - fall back to memory-only.
  }
}

function lsRemove(formId: string) {
  try {
    localStorage.removeItem(lsKey(formId));
  } catch {
    // Ignore
  }
}

export const FormPersistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // In-memory cache so reads don't hit localStorage on every keystroke.
  const store = useRef<Map<string, FormState>>(new Map());

  const get = useCallback((formId: string) => {
    const mem = store.current.get(formId);
    if (mem) return mem;
    const disk = lsRead(formId);
    if (disk) {
      store.current.set(formId, disk);
      return disk;
    }
    return undefined;
  }, []);

  const set = useCallback((formId: string, state: FormState) => {
    store.current.set(formId, state);
    lsWrite(formId, state);
  }, []);

  const clear = useCallback((formId: string) => {
    store.current.delete(formId);
    lsRemove(formId);
  }, []);

  return (
    <FormPersistContext.Provider value={{ get, set, clear }}>
      {children}
    </FormPersistContext.Provider>
  );
};
